import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'src/components/ui/button';
import StickyFooter from 'src/components/ui/sticky-footer';
import {
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Plus,
  Trash2,
  Calendar as CalendarIcon,
} from 'lucide-react';
import Section from 'src/components/ui/section';
import AppSelect from 'src/components/common/AppSelect';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { DynamicFieldArray } from 'src/components/common/DynamicFieldArray';
import { AsyncSingleSelectDropdown } from 'src/components/common/AsyncSingleSelectDropdown';
import {
  usePersonaInfo,
  usePersonaMasterData,
} from 'src/services/api/queries/personaQueries';
import { PersonaService } from 'src/services/api/PersonaService';
import { LocationService } from 'src/services/api/LocationService';
import { OpportunityService } from 'src/services/api/OpportunityService';
import { useToast } from 'src/hooks/use-toast';
import { format, parse } from 'date-fns';

const FormField = ({
  id,
  label,
  children,
  className = '',
  required = false,
}) => (
  <div className={className}>
    <label
      htmlFor={id}
      className="block text-[12.5px] font-[560] leading-5 text-foreground-strong"
    >
      {label}
      {required && <span className="text-[#DC2626] ml-1">*</span>}
    </label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const CreateRequirement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedPersonaId, setSelectedPersonaId] = useState(null);
  const [selectedPersonaOption, setSelectedPersonaOption] = useState(null);
  const [closingDatePopoverOpen, setClosingDatePopoverOpen] = useState(false);
  const [masterData, setMasterData] = useState({
    skillOptions: [],
    certificationOptions: [],
    fieldOfStudyOptions: [],
    workExperienceOptions: [],
    customerTypeOptions: [],
    salaryOptions: [],
    employmentTypeOptions: [
      { value: 'part-time', label: 'Part-time' },
      { value: 'full-time', label: 'Full-time' },
    ],
    careerLevelOptions: [],
    testCollectionOptions: [],
    educationLevelOptions: [
      { value: 'DEGREE', label: 'Degree' },
      { value: 'MASTERS', label: 'Masters' },
    ],
  });

  // React Query hooks
  const { data: personaData, isLoading: isLoadingPersona } =
    usePersonaInfo(selectedPersonaId);
  const { data: masterDataResponse, isLoading: isLoadingMasterData } =
    usePersonaMasterData();

  // Persona fetcher for AsyncSingleSelectDropdown
  const fetchPersonas = useCallback(async ({ page, pageSize, search }) => {
    try {
      const response = await PersonaService.listAllPersonas(page, pageSize);
      const personas = response?.data?.personas || [];
      const pagination = response?.data?.pagination || {};

      // Client-side filtering if search term is provided
      let filteredPersonas = personas;
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filteredPersonas = personas.filter(
          (persona) =>
            persona.title?.toLowerCase().includes(searchLower) ||
            persona.customer_type?.toLowerCase().includes(searchLower)
        );
      }

      return {
        items: filteredPersonas,
        hasNext: pagination?.has_next || false,
      };
    } catch (error) {
      console.error('Error fetching personas:', error);
      return { items: [], hasNext: false };
    }
  }, []);

  const fetchLocations = useCallback(async ({ page, pageSize, search }) => {
    try {
      const resolvedPageSize = page > 1 ? 20 : pageSize;
      const response = await LocationService.listLocations({
        loadAll: true,
        page,
        pageSize: resolvedPageSize,
      });
      const locations = response?.data?.locations || [];

      let filteredLocations = locations;
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filteredLocations = locations.filter((location) =>
          location.value?.toLowerCase().includes(searchLower)
        );
      }

      return {
        items: filteredLocations,
        hasNext: (locations?.length || 0) >= resolvedPageSize,
      };
    } catch (error) {
      console.error('Error fetching locations:', error);
      return { items: [], hasNext: false };
    }
  }, []);

  const form = useForm({
    defaultValues: {
      title: '',
      persona_description: '',
      career_level: '',
      education_level: '',
      minimum_salary: '',
      maximum_salary: '',
      work_experience_id: '',
      test_collection_id: '',
      detailed_job_description: '',
      additional_description: '',
      closing_date: '',
      skills: [{ value: '', isMandatory: false }],
      certifications: [{ value: '', isMandatory: false }],
      fieldOfStudy: [{ value: '', isMandatory: false }],
      requirement_locations: [
        { location: null, employment_type: '', headcount: '' },
      ],
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    trigger,
    watch,
    getValues,
  } = form;
  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({
    control,
    name: 'requirement_locations',
  });

  // Effect to handle master data
  useEffect(() => {
    if (masterDataResponse?.data) {
      const responseData = masterDataResponse.data;
      setMasterData((prevData) => ({
        ...prevData,
        careerLevelOptions:
          responseData?.career_level?.map((item) => ({
            value: item.id.toString(),
            label: item.value,
          })) || [],
        skillOptions:
          responseData?.skills?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
          })) || [],
        certificationOptions:
          responseData?.certifications?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
          })) || [],
        fieldOfStudyOptions:
          responseData?.field_of_study?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
          })) || [],
        workExperienceOptions:
          responseData?.work_experience?.map((item) => ({
            value: item.id.toString(),
            label: item.value,
          })) || [],
        minSalaryOptions:
          responseData?.salary_min_range?.map((item) => ({
            value: item.id.toString(),
            label: `₹${parseInt(item.value, 10).toLocaleString()}`,
          })) || [],
        maxSalaryOptions:
          responseData?.salary_max_range?.map((item) => ({
            value: item.id.toString(),
            label: `₹${parseInt(item.value, 10).toLocaleString()}`,
          })) || [],
        testCollectionOptions:
          responseData?.test_collections?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
          })) || [],
      }));
    }
  }, [masterDataResponse]);

  // Effect to pre-populate form when persona is selected
  useEffect(() => {
    if (
      selectedPersonaId &&
      personaData?.data &&
      masterDataResponse?.data &&
      masterData.careerLevelOptions.length > 0 &&
      masterData.workExperienceOptions.length > 0
    ) {
      const persona = personaData.data;

      try {
        const formData = {
          title: persona.title || '',
          persona_description: persona.persona_description || '',
          minimum_salary: persona.minimum_salary?.toString() || '',
          maximum_salary: persona.maximum_salary?.toString() || '',
          career_level: persona.career_level?.toString() || '',
          work_experience_id: persona.work_experience_id?.toString() || '',
          test_collection_id: persona.test_collection_id?.toString() || '',
          detailed_job_description: persona.detailed_job_description || '',
          additional_description: persona.additional_description || '',
          closing_date: persona.closing_date || '',
          skills: Array.isArray(persona.skills)
            ? persona.skills.map((skill) => ({
                value: skill.id?.toString() || '',
                isMandatory: Boolean(skill.is_mandatory),
              }))
            : [{ value: '', isMandatory: false }],
          certifications: Array.isArray(persona.certifications)
            ? persona.certifications.map((cert) => ({
                value: cert.id?.toString() || '',
                isMandatory: Boolean(cert.is_mandatory),
              }))
            : [{ value: '', isMandatory: false }],
          fieldOfStudy:
            Array.isArray(persona.qualifications) &&
            persona.qualifications[0]?.field_of_study
              ? persona.qualifications[0].field_of_study.map((field) => ({
                  value: field.field_of_study_id?.toString() || '',
                  isMandatory: Boolean(field.is_mandatory),
                }))
              : [{ value: '', isMandatory: false }],
          education_level:
            persona.qualifications?.[0]?.qualification_type || '',
        };

        reset(formData);
      } catch (error) {
        console.error('Error resetting form data:', error);
        toast({
          message: 'Failed to load persona data',
          variant: 'destructive',
        });
      }
    }
  }, [
    selectedPersonaId,
    personaData,
    masterDataResponse,
    masterData.careerLevelOptions,
    masterData.workExperienceOptions,
    reset,
    toast,
  ]);

  const handlePersonaSelect = (option) => {
    if (option) {
      setSelectedPersonaOption(option);
      setSelectedPersonaId(option.value);
    } else {
      setSelectedPersonaOption(null);
      setSelectedPersonaId(null);
      // Reset form when persona is cleared
      reset({
        title: '',
        persona_description: '',
        career_level: '',
        education_level: '',
        minimum_salary: '',
        maximum_salary: '',
        work_experience_id: '',
        test_collection_id: '',
        detailed_job_description: '',
        additional_description: '',
        closing_date: '',
        skills: [{ value: '', isMandatory: false }],
        certifications: [{ value: '', isMandatory: false }],
        fieldOfStudy: [{ value: '', isMandatory: false }],
        requirement_locations: [
          { location: null, employment_type: '', headcount: '' },
        ],
      });
    }
  };

  const handleBack = () => {
    navigate('/opportunities');
  };

  const formatEmploymentType = (value) => {
    if (!value) return '';
    const normalized = String(value).toLowerCase();
    if (normalized === 'full-time' || normalized === 'full time') {
      return 'Full-Time';
    }
    if (normalized === 'part-time' || normalized === 'part time') {
      return 'Part-Time';
    }
    return value;
  };

  const onSubmit = async (formData) => {
    try {
      const resolvedCareerLevel =
        masterData.careerLevelOptions.find(
          (option) => option.value === formData.career_level
        )?.label || formData.career_level;
      const payload = {
        organization_id: personaData?.data?.organization_id || 1,
        template_type: '1',
        title: formData.title,
        career_level: resolvedCareerLevel,
        detailed_job_description: formData.detailed_job_description,
        additional_requirements: formData.additional_description || '',
        minimum_salary: Number(formData.minimum_salary),
        maximum_salary: Number(formData.maximum_salary),
        work_experience_id: Number(formData.work_experience_id),
        test_collection_id: Number(formData.test_collection_id),
        closing_date: formData.closing_date || '',
        qualification_requirements: [
          {
            qualification_type: formData.education_level,
            field_of_study: (formData.fieldOfStudy || [])
              .filter((field) => field.value)
              .map((field) => ({
                field_of_study_id: Number(field.value),
                is_mandatory: Boolean(field.isMandatory),
              })),
          },
        ],
        locations: (formData.requirement_locations || [])
          .filter((entry) => entry?.location?.value)
          .map((entry) => ({
            location_id: Number(entry.location.value),
            headcount: Number(entry.headcount),
            employment_type: formatEmploymentType(entry.employment_type),
          })),
        certifications: (formData.certifications || [])
          .filter((cert) => cert.value)
          .map((cert) => ({
            certification_id: Number(cert.value),
            is_mandatory: Boolean(cert.isMandatory),
          })),
        skills: (formData.skills || [])
          .filter((skill) => skill.value)
          .map((skill) => ({
            skill_id: Number(skill.value),
            is_mandatory: Boolean(skill.isMandatory),
          })),
      };

      const response = await OpportunityService.createMockRequirement(payload);
      const createdRequirement = response?.data || response;
      const requirementId =
        createdRequirement?.requirement_id || createdRequirement?.id;

      if (!requirementId) {
        throw new Error('Requirement ID missing in response');
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['opportunities'] }),
        queryClient.invalidateQueries({ queryKey: ['ta', 'summary'] }),
      ]);

      toast({
        variant: 'success',
        message: 'Requirement created successfully',
      });

      navigate('/opportunities');
    } catch (error) {
      console.error('Requirement creation error:', error);
      toast({
        message: 'Failed to create requirement. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Money helpers
  const formatCurrency = (val) => {
    if (val === undefined || val === null || val === '') return '';
    const digits = String(val).replace(/[^0-9]/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const parseCurrency = (val) =>
    val ? String(val).replace(/[^0-9]/g, '') : '';

  // Validation logic
  const getMissingFieldsCount = (formValues) => {
    let missingCount = 0;

    if (!formValues.title) missingCount += 1;
    if (!formValues.career_level) missingCount += 1;
    if (!formValues.education_level) missingCount += 1;
    if (!formValues.minimum_salary) missingCount += 1;
    if (!formValues.maximum_salary) missingCount += 1;
    if (!formValues.work_experience_id) missingCount += 1;
    if (!formValues.test_collection_id) missingCount += 1;
    if (!formValues.detailed_job_description) missingCount += 1;

    if (Array.isArray(formValues.certifications)) {
      formValues.certifications.forEach((cert) => {
        if (cert?.isMandatory === true && !cert?.value) {
          missingCount += 1;
        }
      });
    }

    if (Array.isArray(formValues.skills)) {
      formValues.skills.forEach((skill) => {
        if (skill?.isMandatory === true && !skill?.value) {
          missingCount += 1;
        }
      });
    }

    if (Array.isArray(formValues.requirement_locations)) {
      formValues.requirement_locations.forEach((loc) => {
        if (!loc?.location?.value) missingCount += 1;
        if (!loc?.employment_type) missingCount += 1;
        if (!loc?.headcount) missingCount += 1;
      });
    }

    return missingCount;
  };

  const watchedValues = watch();
  const missingFieldsCount = getMissingFieldsCount(watchedValues);
  const requirementLocations = watchedValues?.requirement_locations || [];
  const lastLocation =
    requirementLocations.length > 0
      ? requirementLocations[requirementLocations.length - 1]
      : undefined;
  const isLastLocationComplete =
    !!lastLocation?.location?.value &&
    !!lastLocation?.headcount &&
    !!lastLocation?.employment_type;

  const handleNextClick = () => {
    if (missingFieldsCount === 0) {
      handleSubmit(onSubmit)();
    } else {
      trigger();
    }
  };

  const isLoading = isLoadingPersona || isLoadingMasterData;
  return (
    <div className="text-left">
      <PageHeader
        title="Create Requirement"
        actions={
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={handleBack}
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
            Back
          </Button>
        }
      />

      <div className="items-center justify-center p-6 mt-0 bg-[#F8FAFC] pb-24">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="text-left w-full space-y-10"
        >
          <Section title="Select Persona">
            <FormField id="persona" className="w-full mb-4">
              <AsyncSingleSelectDropdown
                fetcher={fetchPersonas}
                getOptionLabel={(persona) => persona.title || 'Unknown'}
                getOptionValue={(persona) => persona.id}
                value={selectedPersonaOption}
                onChange={handlePersonaSelect}
                placeholder="Search and select a persona to pre-fill the form..."
                pageSize={10}
              />
              {selectedPersonaOption && (
                <p className="mt-2 text-xs text-gray-500">
                  {selectedPersonaOption.item?.persona_description ||
                    selectedPersonaOption.item?.description ||
                    'No persona description available.'}
                </p>
              )}
            </FormField>
          </Section>
          <Section title="Job Requirements">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                id="title"
                label="Job Title"
                error={errors.title}
                required
                className="w-full mb-4"
              >
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'Job Title is required' }}
                  render={({ field: { onChange, value } }) => (
                    <input
                      type="text"
                      onChange={onChange}
                      value={value}
                      className={`w-full rounded-lg border bg-card px-3.5 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:ring-[3px] ${
                        errors.title
                          ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                          : 'border-input focus:border-primary focus:ring-primary/[0.08]'
                      }`}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="career_level"
                label="Career Level"
                error={errors.career_level}
                required
              >
                <Controller
                  name="career_level"
                  control={control}
                  rules={{ required: 'Career Level is required' }}
                  render={({ field }) => (
                    <AppSelect
                      options={masterData.careerLevelOptions || []}
                      onValueChange={field.onChange}
                      value={field.value}
                      placeholder="Select Career Level"
                      error={!!errors.career_level}
                      className="w-full"
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="flex flex-col space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <FormField
                  id="minimum_salary"
                  label="Minimum Salary"
                  error={errors.minimum_salary}
                  required
                >
                  <Controller
                    name="minimum_salary"
                    control={control}
                    rules={{ required: 'Minimum Salary is required' }}
                    render={({ field: { onChange, value } }) => (
                      <input
                        type="text"
                        inputMode="numeric"
                        onChange={(e) =>
                          onChange(parseCurrency(e.target.value))
                        }
                        value={formatCurrency(value)}
                        className={`w-full rounded-lg border bg-card px-3.5 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:ring-[3px] ${
                          errors.minimum_salary
                            ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                            : 'border-input focus:border-primary focus:ring-primary/[0.08]'
                        }`}
                      />
                    )}
                  />
                </FormField>
                <FormField
                  id="maximum_salary"
                  label="Maximum Salary"
                  error={errors.maximum_salary}
                  required
                >
                  <Controller
                    name="maximum_salary"
                    control={control}
                    rules={{
                      required: 'Maximum Salary is required',
                      validate: (v) => {
                        const min = parseInt(
                          parseCurrency(getValues('minimum_salary') || ''),
                          10
                        );
                        const max = parseInt(parseCurrency(v || ''), 10);
                        if (Number.isNaN(min) || Number.isNaN(max)) return true;
                        return (
                          max > min ||
                          'Maximum Salary must be greater than Minimum Salary'
                        );
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <input
                        type="text"
                        inputMode="numeric"
                        onChange={(e) =>
                          onChange(parseCurrency(e.target.value))
                        }
                        value={formatCurrency(value)}
                        className={`w-full rounded-lg border bg-card px-3.5 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:ring-[3px] ${
                          errors.maximum_salary
                            ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                            : 'border-input focus:border-primary focus:ring-primary/[0.08]'
                        }`}
                      />
                    )}
                  />
                </FormField>
              </div>

              <FormField
                id="detailed_job_description"
                label="Job Description"
                error={errors.detailed_job_description}
                required
                className="w-full"
              >
                <Controller
                  name="detailed_job_description"
                  control={control}
                  rules={{
                    required: 'Job Description is required',
                    minLength: {
                      value: 10,
                      message: 'Description must be at least 50 characters',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <textarea
                      rows={4}
                      onChange={onChange}
                      value={value}
                      className={`w-full rounded-lg border bg-card px-3.5 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:ring-[3px] ${
                        errors.detailed_job_description
                          ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                          : 'border-input focus:border-primary focus:ring-primary/[0.08]'
                      }`}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="additional_description"
                label="Additional Description"
                className="w-full"
              >
                <Controller
                  name="additional_description"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <textarea
                      rows={3}
                      onChange={onChange}
                      value={value}
                      className="w-full rounded-lg border border-input bg-card px-3.5 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]"
                    />
                  )}
                />
              </FormField>
            </div>
          </Section>
          <Section title="Locations & Headcount">
            <div className="space-y-3">
              {locationFields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-1 md:grid-cols-[2fr_2fr_2fr_2fr_0.5fr] gap-4 items-end"
                >
                  <div className="md:col-span-2">
                    <FormField
                      id={`requirement_locations.${index}.location`}
                      label="Location"
                      required
                    >
                      <Controller
                        name={`requirement_locations.${index}.location`}
                        control={control}
                        rules={{
                          validate: (v) => !!v?.value || 'Location is required',
                        }}
                        render={({ field: locationField }) => (
                          <AsyncSingleSelectDropdown
                            fetcher={fetchLocations}
                            getOptionLabel={(location) =>
                              location.value || 'Unknown'
                            }
                            getOptionValue={(location) => location.id}
                            value={locationField.value}
                            onChange={locationField.onChange}
                            placeholder="Select Location"
                            searchPlaceholder="Search locations..."
                            pageSize={4}
                            loadMoreMode="button"
                            loadMoreLabel="Load More"
                            valueDisplay="text"
                            showSelectionIndicator={false}
                            showClearButton={false}
                          />
                        )}
                      />
                    </FormField>
                  </div>
                  <FormField
                    id={`requirement_locations.${index}.headcount`}
                    label="Headcount"
                    required
                  >
                    <Controller
                      name={`requirement_locations.${index}.headcount`}
                      control={control}
                      rules={{ required: 'Headcount is required' }}
                      render={({ field: headcountField }) => (
                        <input
                          type="number"
                          min="0"
                          inputMode="numeric"
                          onChange={headcountField.onChange}
                          value={headcountField.value}
                          className={`w-full rounded-lg border bg-card px-3.5 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:ring-[3px] ${
                            errors?.requirement_locations?.[index]?.headcount
                              ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                              : 'border-input focus:border-primary focus:ring-primary/[0.08]'
                          }`}
                        />
                      )}
                    />
                  </FormField>
                  <FormField
                    id={`requirement_locations.${index}.employment_type`}
                    label="Employment Type"
                    required
                  >
                    <Controller
                      name={`requirement_locations.${index}.employment_type`}
                      control={control}
                      rules={{ required: 'Employment Type is required' }}
                      render={({ field: employmentField }) => (
                        <AppSelect
                          options={masterData.employmentTypeOptions || []}
                          onValueChange={employmentField.onChange}
                          value={employmentField.value}
                          placeholder="Select Employment Type"
                          error={
                            !!errors?.requirement_locations?.[index]
                              ?.employment_type
                          }
                          className="w-full"
                        />
                      )}
                    />
                  </FormField>
                  <div className="flex items-center">
                    {locationFields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLocation(index)}
                        className="text-gray-500 hover:text-red-600 border-none outline-none"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <div>
                <Button
                  type="button"
                  variant="addField"
                  size="sm"
                  disabled={!isLastLocationComplete}
                  onClick={() =>
                    appendLocation({
                      location: null,
                      employment_type: '',
                      headcount: '',
                    })
                  }
                  className={`min-w-fit max-w-xs border-dashed border-gray-300 whitespace-nowrap py-4 ${
                    isLastLocationComplete
                      ? 'text-gray-600 hover:bg-muted'
                      : 'cursor-not-allowed text-gray-400'
                  }`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add entry
                </Button>
              </div>
            </div>
          </Section>
          <div className="space-y-4">
            <Section title="Eligibility">
              <FormField
                id="education_level"
                label="Education Level"
                error={errors.education_level}
                className="mb-4"
                required
              >
                <Controller
                  name="education_level"
                  control={control}
                  rules={{ required: 'Education Level is required' }}
                  render={({ field }) => (
                    <AppSelect
                      options={masterData.educationLevelOptions || []}
                      onValueChange={field.onChange}
                      value={field.value}
                      placeholder="Select Education Level"
                      error={!!errors.education_level}
                      className="w-full"
                    />
                  )}
                />
              </FormField>
              <FormField
                id="work_experience_id"
                label="Work Experience"
                error={errors.work_experience_id}
                className="mb-4"
                required
              >
                <Controller
                  name="work_experience_id"
                  control={control}
                  rules={{ required: 'Work Experience is required' }}
                  render={({ field }) => (
                    <AppSelect
                      options={masterData.workExperienceOptions || []}
                      onValueChange={field.onChange}
                      value={field.value}
                      placeholder="Select Experience"
                      error={!!errors.work_experience_id}
                      className="w-full"
                    />
                  )}
                />
              </FormField>
              <DynamicFieldArray
                control={control}
                name="fieldOfStudy"
                options={masterData.fieldOfStudyOptions}
                label="Field of Study"
                error={errors.fieldOfStudy}
                isOptional
              />
            </Section>
            <Section title="Skills and Knowledge">
              <DynamicFieldArray
                control={control}
                name="skills"
                options={masterData.skillOptions}
                label=""
                error={errors.skills}
                isOptional
              />
            </Section>
            <Section title="Professional Certifications">
              <DynamicFieldArray
                label=""
                control={control}
                name="certifications"
                options={masterData.certificationOptions}
                error={errors.certifications}
                isOptional
              />
            </Section>
            <Section title="Test & Closing Date">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField
                  id="test_collection_id"
                  label="Test Collection"
                  error={errors.test_collection_id}
                  className="md:col-span-3"
                >
                  <Controller
                    name="test_collection_id"
                    control={control}
                    rules={{ required: 'Test Collection is required' }}
                    render={({ field }) => (
                      <AppSelect
                        options={masterData.testCollectionOptions || []}
                        onValueChange={field.onChange}
                        value={field.value}
                        placeholder="Select Test Collection"
                        error={!!errors.test_collection_id}
                        className="w-full"
                      />
                    )}
                  />
                </FormField>
                <FormField
                  id="closing_date"
                  label="Closing Date"
                  className="w-full md:col-span-1"
                >
                  <Controller
                    name="closing_date"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Popover
                        open={closingDatePopoverOpen}
                        onOpenChange={setClosingDatePopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <div className="relative">
                            <div
                              className={`flex h-9 w-full items-center rounded-lg border border-input bg-card px-3 py-2 pr-10 text-sm cursor-pointer transition-[border-color,box-shadow] ${
                                value
                                  ? 'text-foreground-strong'
                                  : 'text-muted-foreground'
                              }`}
                            >
                              {value
                                ? format(
                                    parse(value, 'yyyy-MM-dd', new Date()),
                                    'dd MMM yyyy'
                                  )
                                : 'Select date'}
                            </div>
                            <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none">
                              <CalendarIcon className="h-4 w-4 text-gray-500" />
                            </div>
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                          <Calendar
                            mode="single"
                            selected={
                              value
                                ? parse(value, 'yyyy-MM-dd', new Date())
                                : undefined
                            }
                            onSelect={(selectedDate) => {
                              onChange(
                                selectedDate
                                  ? format(selectedDate, 'yyyy-MM-dd')
                                  : ''
                              );
                              setClosingDatePopoverOpen(false);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                </FormField>
              </div>
            </Section>
          </div>
        </form>
        <StickyFooter>
          <div className="flex items-center justify-between px-6 py-2.5">
            <div className="flex items-center gap-4">
              {missingFieldsCount > 0 && (
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-gray-500">
                    {missingFieldsCount} required field
                    {missingFieldsCount !== 1 ? 's' : ''} missing
                  </span>
                </div>
              )}
              {missingFieldsCount === 0 && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-500">Ready to submit</span>
                </div>
              )}
            </div>
            <Button
              size="sm"
              onClick={handleNextClick}
              disabled={isLoading}
              className={`text-xs px-6 py-2 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {(() => {
                if (isLoading) {
                  return 'Creating Requirement...';
                }
                if (missingFieldsCount === 0) {
                  return 'Confirm & Submit';
                }
                return 'Next';
              })()}
            </Button>
          </div>
        </StickyFooter>
      </div>
    </div>
  );
};

export default CreateRequirement;
